import { useEffect, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Undo2,
  Unlink,
} from 'lucide-react';
import {
  CONTENT_LOCALES,
  translationStatus,
  type ContentLocale,
  type TranslatableString,
} from '@geoic/shared';
import { cn } from '@/lib/cn.js';

const LOCALE_LABELS: Record<ContentLocale, string> = { uz: 'UZ', ru: 'RU', en: 'EN' };

export type RichTextInputProps = {
  label: string;
  value: TranslatableString;
  onChange: (value: TranslatableString) => void;
  hint?: string;
};

/**
 * Uch tilli WYSIWYG (TZ 7.1: yangiliklar, `rich-text` bloki).
 *
 * Har bir til uchun ALOHIDA muharrir yaratilmaydi — bittasi til
 * almashganda qayta to'ldiriladi. Uchtasini birdan ushlab turish
 * xotirani va ishga tushirish vaqtini uch barobar oshirardi.
 *
 * Chiqadigan HTML serverda YANA tozalanadi (`SanitizeService`) —
 * mijozdagi muharrirga ishonib bo'lmaydi.
 */
export function RichTextInput({ label, value, onChange, hint }: RichTextInputProps) {
  const [active, setActive] = useState<ContentLocale>('uz');
  const filled = translationStatus(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // H1 sahifada BITTA bo'lishi kerak va u sarlavhadan keladi —
        // matn ichida ikkinchi H1 SEO ni buzardi (B11).
        heading: { levels: [2, 3] },
      }),
      Link.configure({ openOnClick: false, autolink: false }),
    ],
    content: value[active],
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-40 px-3 py-2 focus:outline-none',
        'aria-label': `${label} (${LOCALE_LABELS[active]})`,
      },
    },
    onUpdate: ({ editor: current }) => {
      const html = current.getHTML();
      // TipTap bo'sh matnni `<p></p>` deb qaytaradi — u «to'ldirilgan»
      // deb hisoblanib, tarjima nuqtasi noto'g'ri yashirinardi.
      onChange({ ...value, [active]: html === '<p></p>' ? '' : html });
    },
  });

  // Til almashganda muharrir tarkibi almashadi. `emitUpdate: false` —
  // aks holda `onUpdate` ishga tushib, endigina ochilgan tilga oldingi
  // tilning matnini yozib qo'yardi.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value[active] || '';
    if (current !== next) editor.commands.setContent(next, { emitUpdate: false });
    // `value` ATAYLAB bog'liqlikda yo'q: u har harf terilganda o'zgaradi
    // va effekt muharrir tarkibini qayta o'rnatib, kursorni matn boshiga
    // otib yuborardi.
  }, [active, editor]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-n-800">{label}</span>

        <div role="tablist" aria-label={`${label} — til`} className="flex gap-1">
          {CONTENT_LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              role="tab"
              aria-selected={active === locale}
              onClick={() => setActive(locale)}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 text-xs font-medium transition-colors',
                active === locale
                  ? 'bg-brand-600 text-white'
                  : 'bg-n-100 text-n-600 hover:bg-n-200',
              )}
            >
              {LOCALE_LABELS[locale]}
              {!filled[locale] && (
                <span
                  aria-label="to‘ldirilmagan"
                  className={cn(
                    'size-1.5 rounded-full',
                    active === locale ? 'bg-white/70' : 'bg-warning-600',
                  )}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden border border-n-200 focus-within:border-brand-500">
        {editor && <Toolbar editor={editor} />}
        <EditorContent editor={editor} />
      </div>

      {hint && <p className="text-sm text-n-600">{hint}</p>}
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-n-200 bg-n-50 px-1 py-1">
      <ToolbarButton
        label="Qalin"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="size-4" aria-hidden />
      </ToolbarButton>
      <ToolbarButton
        label="Kursiv"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="size-4" aria-hidden />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Sarlavha 2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="size-4" aria-hidden />
      </ToolbarButton>
      <ToolbarButton
        label="Sarlavha 3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="size-4" aria-hidden />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Belgili ro‘yxat"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-4" aria-hidden />
      </ToolbarButton>
      <ToolbarButton
        label="Raqamli ro‘yxat"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-4" aria-hidden />
      </ToolbarButton>
      <ToolbarButton
        label="Iqtibos"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="size-4" aria-hidden />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Havola"
        active={editor.isActive('link')}
        onClick={() => {
          const previous = (editor.getAttributes('link').href as string | undefined) ?? '';
          const href = window.prompt('Havola manzili', previous);
          if (href === null) return;
          if (href === '') {
            editor.chain().focus().unsetLink().run();
            return;
          }
          editor.chain().focus().setLink({ href }).run();
        }}
      >
        <LinkIcon className="size-4" aria-hidden />
      </ToolbarButton>
      <ToolbarButton
        label="Havolani olib tashlash"
        disabled={!editor.isActive('link')}
        onClick={() => editor.chain().focus().unsetLink().run()}
      >
        <Unlink className="size-4" aria-hidden />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label="Bekor qilish"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="size-4" aria-hidden />
      </ToolbarButton>
      <ToolbarButton
        label="Qaytarish"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="size-4" aria-hidden />
      </ToolbarButton>
    </div>
  );
}

function ToolbarButton({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        ' p-1.5 text-n-600 transition-colors',
        active && 'bg-brand-100 text-brand-700',
        !active && !disabled && 'hover:bg-n-200',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-n-200" aria-hidden />;
}
